import useRoleNavigate from '@brands/hooks/useRoleNavigate';
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Divider } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { formatDateTime } from '../utils/consultationHelpers';
import styles from '../styles.module.scss';
import { CaseType, ICase } from '@brands/services/cases/types/ICase';
import { UserProfile, UserRoleName } from '@brands/services/identity/types/UserProfile';
import { getFriendlyName } from '@brands/Utils/getFriendlyName';
import { getStatusColorStyles } from '@brands/Utils/getColorForStatus';
import { getTextForStatus } from '@brands/Utils/getTextForStatus';
import SmallSelectionModal from '../Modals/SmallSelectionModal/SmallSelectionModal';
import { canRoleCancelCase, canRoleCompleteCase, checkEditValid } from '@brands/Utils/getCollapsibleHeader';
import ChangeConsultDateTimeModal from '../Modals/ChangeConsultDateTimeModal/ChangeConsultDateTimeModal';
import { CasePayload } from '@brands/services/cases/createNewCase';
import { config } from '@brands/config/config';
import { getLinkedCases, LinkedCasesResponse } from '@brands/services/cases/multiStage';
import { caseTypeLabel } from '@brands/Dashboard/MultiStage/multiStageUi';
import { tokens } from '@brands/Dashboard/MultiStage/multiStageTokens';

type Props = {
  thisCase: ICase;
  userInfo: UserProfile;
  timeZone: string;
  changeCaseType: (selectedCaseType: string) => Promise<void>;
  changeCaseStatus: (status: 'completed' | 'canceled', reason?: string, reasonOther?: string) => Promise<void>;
  setCase: React.Dispatch<React.SetStateAction<ICase>>;
  editCase: (case_id: number, body: CasePayload) => Promise<ICase | null>;
  rescheduleCase: (case_id: number, video_conference_date: string) => Promise<ICase | null>;
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
};

const CaseDetailsCard = ({
  thisCase,
  userInfo,
  timeZone,
  changeCaseType,
  changeCaseStatus,
  setCase,
  editCase,
  rescheduleCase,
  loading,
  setLoading,
}: Props): JSX.Element => {
  const roleNavigate = useRoleNavigate();

  const [linkedCases, setLinkedCases] = useState<LinkedCasesResponse | null>(null);
  const [linksFailed, setLinksFailed] = useState(false);

  useEffect(() => {
    setLinkedCases(null);
    setLinksFailed(false);
    if (!config.features.multiStage || !thisCase.id) return undefined;
    let active = true;
    let inFlight = false;
    const refresh = async (): Promise<void> => {
      if (inFlight) return;
      inFlight = true;
      try {
        const result = await getLinkedCases(Number(thisCase.id));
        if (active) {
          setLinkedCases(result);
          setLinksFailed(false);
        }
      } catch {
        if (active) {
          setLinkedCases(null);
          setLinksFailed(true);
        }
      } finally {
        inFlight = false;
      }
    };
    void refresh();
    const timer = window.setInterval(() => {
      void refresh();
    }, 30000);
    window.addEventListener('focus', refresh);
    return () => {
      active = false;
      window.clearInterval(timer);
      window.removeEventListener('focus', refresh);
    };
  }, [thisCase.id, thisCase.updated_at]);

  const linksResolved = linkedCases?.case_id === Number(thisCase.id);
  const siblingCases = linksResolved ? linkedCases.linked_cases : [];
  const isMultiStageCase = config.features.multiStage && linksResolved && linkedCases.case_journey_id != null;

  const [isConsultTypeModalOpen, setIsConsultTypeModalOpen] = useState(false);
  const [pendingConsultType, setPendingConsultType] = useState<string>('');

  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusStep, setStatusStep] = useState<'STATUS' | 'REASON'>('STATUS');
  const [selectedStatus, setSelectedStatus] = useState<'COMPLETE' | 'CANCEL' | ''>('');
  const [selectedReason, setSelectedReason] = useState<string>('');
  // Free-text reason captured when the user picks "Other" in the
  // cancel-reason step. Required server-side (cases/cancel_case.py
  // returns "Please include a message for 'Other'." otherwise).
  const [reasonOtherText, setReasonOtherText] = useState<string>('');

  const [isChangeConsultTimeModalOpen, setIsChangeConsultTimeModalOpen] = useState(false);
  const isCollector = userInfo.role.name === UserRoleName.Collector;
  const isCollectorAssignee = isCollector && Number(thisCase.collector_id) === Number(userInfo.id);
  const isCareAdvisorAssignee =
    userInfo.role.name === UserRoleName.CareAdvisor && Number(thisCase.care_advisor_id) === Number(userInfo.id);
  const canCancelCase = canRoleCancelCase(userInfo.role.name);
  const canCompleteCase = canRoleCompleteCase(userInfo.role.name);
  const canStaffReschedule =
    (userInfo.role.name === UserRoleName.CareAdvisor && Number(thisCase.care_advisor_id) === Number(userInfo.id)) ||
    userInfo.role.name === UserRoleName.Admin ||
    userInfo.role.name === UserRoleName.SuperAdmin;

  const cancelReasonOptions = useMemo(
    () => [
      { value: 'PATIENT_NO_LONGER_AVAILABLE', label: 'The patient is no longer available' },
      { value: 'PATIENT_NO_LONGER_NEEDS_HELP', label: 'The patient no longer needs help' },
      { value: 'PATIENT_RECEIVED_HELP_ELSEWHERE', label: 'The patient received help elsewhere' },
      { value: 'PATIENT_PLATFORM_PROBLEMS', label: 'The patient is having problems with the app' },
      { value: 'PATIENT_DID_NOT_ATTEND', label: "The patient didn't attend the appointment" },
      { value: 'PROVIDER_DID_NOT_ATTEND', label: "The provider didn't attend the appointment" },
      { value: 'OTHER', label: 'Other' },
    ],
    [],
  );

  const openChangeStatusModal = (): void => {
    setStatusStep('STATUS');
    setSelectedStatus('');
    setSelectedReason('');
    setReasonOtherText('');
    setShowStatusModal(true);
  };

  const handleCloseStatusModal = (): void => {
    setShowStatusModal(false);
    setStatusStep('STATUS');
    setSelectedStatus('');
    setSelectedReason('');
    setReasonOtherText('');
  };

  const handleStatusPrimaryClick = async (): Promise<void> => {
    if (loading) return;

    if (statusStep === 'STATUS') {
      if (!selectedStatus) return;

      if (selectedStatus === 'CANCEL') {
        setStatusStep('REASON');
        return;
      }

      setLoading(true);
      try {
        await changeCaseStatus('completed');
        handleCloseStatusModal();
      } finally {
        setLoading(false);
      }

      return;
    }

    if (!selectedReason) return;
    if (selectedReason === 'OTHER' && !reasonOtherText.trim()) return;

    setLoading(true);
    try {
      await changeCaseStatus(
        'canceled',
        selectedReason,
        selectedReason === 'OTHER' ? reasonOtherText.trim() : undefined,
      );
      handleCloseStatusModal();
    } finally {
      setLoading(false);
    }
  };

  const caseIsActive = thisCase.status !== 'completed' && thisCase.status !== 'canceled';
  const isCaseOwnerStaff = isCareAdvisorAssignee || isCollectorAssignee;
  const statusOptions = [
    ...(isCaseOwnerStaff || !canCompleteCase ? [] : [{ value: 'COMPLETE', label: 'Complete' }]),
    ...(canCancelCase ? [{ value: 'CANCEL', label: 'Canceled' }] : []),
  ];
  const canChangeStatus =
    caseIsActive &&
    statusOptions.length > 0 &&
    (userInfo.role.name === UserRoleName.Admin ||
      userInfo.role.name === UserRoleName.SuperAdmin ||
      userInfo.role.name === UserRoleName.OrganizationAdmin ||
      isCaseOwnerStaff);
  const canRescheduleCase = thisCase.status !== 'canceled';

  const consultationTypeOptions = useMemo(
    () =>
      [
        {
          value: CaseType.video_call_scheduled,
          label: 'Scheduled video call',
        },
        {
          value: CaseType.phone_call_scheduled,
          label: 'Scheduled phone call',
        },
        {
          value: CaseType.one_report,
          label: 'Photo review',
        },
        {
          value: CaseType.second_opinion,
          label: 'Second opinion',
        },
      ].filter((option) => option.value !== thisCase.type),
    [thisCase.type],
  );

  const openConsultTypeModal = (): void => {
    setPendingConsultType(consultationTypeOptions[0]?.value || '');
    setIsConsultTypeModalOpen(true);
  };

  const handleApplyConsultType = async (value: string): Promise<void> => {
    setLoading(true);

    try {
      await changeCaseType(value);
      setIsConsultTypeModalOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyStatus = async (): Promise<void> => {
    if (!selectedStatus) return;

    if (selectedStatus === 'CANCEL') {
      setStatusStep('REASON');
      return;
    }

    setLoading(true);

    try {
      await changeCaseStatus('completed');
      setShowStatusModal(false);
    } finally {
      setLoading(false);
    }
  };

  const handleContinueCancel = async (): Promise<void> => {
    if (!selectedReason) return;
    if (selectedReason === 'OTHER' && !reasonOtherText.trim()) return;

    setLoading(true);

    try {
      await changeCaseStatus(
        'canceled',
        selectedReason,
        selectedReason === 'OTHER' ? reasonOtherText.trim() : undefined,
      );
      setShowStatusModal(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.consultationPageRightSideFrameCaseDetails}>
      <div className={styles.consultationPageRightSideFrameCaseDetailsTitle}>
        <div>
          <span>Case details</span>
          {userInfo.role.name === UserRoleName.Provider && Number(thisCase.provider_id) !== Number(userInfo.id) && (
            <span role="status">Read-only</span>
          )}
          {(userInfo.role.name === UserRoleName.Admin ||
            userInfo.role.name === UserRoleName.SuperAdmin ||
            userInfo.role.name === UserRoleName.OrganizationAdmin ||
            userInfo.role.name === UserRoleName.CareAdvisor) && (
            <Button
              variant="text"
              className={styles.viewActivityLink}
              role="button"
              tabIndex={0}
              onClick={() => roleNavigate(`/consultation-page/${thisCase.id}/case-activity`)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  roleNavigate(`/consultation-page/${thisCase.id}/case-activity`);
                }
              }}
            >
              View case activity
            </Button>
          )}
        </div>
        <Divider orientation="horizontal" flexItem className={styles.divider} />
      </div>

      <div className={styles.consultationPageRightSideFrameCaseDetails1}>
        <span className={styles.caseDateWithAction}>
          {(thisCase.type === CaseType.video_call_scheduled ||
            thisCase.type === CaseType.phone_call_scheduled ||
            thisCase.type === CaseType.oral_health_coaching) && (
            <>
              {thisCase.video_conference_date
                ? formatDateTime(thisCase.video_conference_date, timeZone)
                : formatDateTime(thisCase.created_at, timeZone)}

              {(checkEditValid(thisCase, userInfo) || isCollectorAssignee || canStaffReschedule) &&
                canRescheduleCase && (
                  <button
                    type="button"
                    className={styles.rescheduleLink}
                    onClick={() => setIsChangeConsultTimeModalOpen(true)}
                  >
                    <CalendarTodayIcon />
                    Reschedule
                  </button>
                )}
              <ChangeConsultDateTimeModal
                open={canRescheduleCase && isChangeConsultTimeModalOpen}
                onClose={() => setIsChangeConsultTimeModalOpen(false)}
                thisCase={thisCase}
                setCase={setCase}
                rescheduleCase={rescheduleCase}
                setLoading={setLoading}
                providerId={
                  thisCase.type === CaseType.oral_health_coaching ? thisCase.care_advisor_id : thisCase.provider_id
                }
              />
            </>
          )}
        </span>

        <div className={styles.consultationPageRightSideFrameCaseDetails1Div}>
          <span className={styles.caseTypeWithDropdown}>
            {getFriendlyName(thisCase.type)}
            {isMultiStageCase ? ' (Multi-stage case)' : ''}

            {(userInfo.role.name === UserRoleName.Admin ||
              userInfo.role.name === UserRoleName.SuperAdmin ||
              userInfo.role.name === UserRoleName.OrganizationAdmin ||
              userInfo.role.name === UserRoleName.CareAdvisor) &&
              caseIsActive &&
              thisCase.type !== CaseType.care_coordination &&
              thisCase.type !== CaseType.oral_health_coaching && (
                <KeyboardArrowDownIcon className={styles.editIconArrow} onClick={openConsultTypeModal} />
              )}
          </span>

          <Divider orientation="vertical" flexItem className={styles.divider} />
          <span>Case ID: {thisCase.id}</span>
        </div>
      </div>

      <div className={styles.consultationPageRightSideFrameCaseDetails2}>
        <span>Case due</span>
        <div className={styles.consultationPageRightSideFrameCaseDetails2Div}>
          <span>{thisCase.due_date ? formatDateTime(thisCase.due_date, timeZone) : '-'}</span>
        </div>
      </div>

      {thisCase.completed_at && (
        <div className={styles.consultationPageRightSideFrameCaseDetails2}>
          <span>Completed date</span>
          <div className={styles.consultationPageRightSideFrameCaseDetails2Div}>
            <span>{thisCase.completed_at ? formatDateTime(thisCase.completed_at, timeZone) : '-'}</span>
          </div>
        </div>
      )}

      <div className={styles.consultationPageRightSideFrameCaseDetailsStatus}>
        <span>Status</span>

        <span
          className={styles.consultationPageRightSideFrameCaseDetailsStatusBubble}
          style={getStatusColorStyles(thisCase.status)}
        >
          {getTextForStatus(userInfo.role.name, thisCase.type, thisCase.status)}

          {canChangeStatus && (
            <KeyboardArrowDownIcon className={styles.editIconArrow} onClick={openChangeStatusModal} />
          )}
        </span>
      </div>

      {config.features.multiStage && (siblingCases.length > 0 || linksFailed) && (
        <div className={styles.consultationPageRightSideFrameCaseDetails2}>
          <span>Linked Cases</span>
          <div className={styles.consultationPageRightSideFrameCaseDetails2Div} style={{ display: 'block' }}>
            {linksFailed ? (
              <span role="status">Unable to load linked cases.</span>
            ) : (
              siblingCases.map((sibling, index) => (
                <React.Fragment key={sibling.id}>
                  {index > 0 && ', '}
                  <Link
                    to={`/${userInfo.role.name.toLowerCase()}/consultation-page/${sibling.id}`}
                    style={{ color: tokens.purple, textDecoration: 'underline' }}
                  >
                    {caseTypeLabel(sibling.type)} (#{sibling.id})
                  </Link>
                </React.Fragment>
              ))
            )}
          </div>
        </div>
      )}

      <SmallSelectionModal
        open={isConsultTypeModalOpen}
        title="Change consultation type"
        options={consultationTypeOptions}
        selectedValue={pendingConsultType}
        onChange={(value: string) => setPendingConsultType(value)}
        primaryLabel="Apply"
        onPrimaryClick={async () => {
          if (!pendingConsultType) return;
          await handleApplyConsultType(pendingConsultType);
        }}
        onClose={() => setIsConsultTypeModalOpen(false)}
        loading={loading}
      />

      <SmallSelectionModal
        open={showStatusModal}
        title={statusStep === 'STATUS' ? 'Update status' : 'Why is this case being canceled?'}
        options={statusStep === 'STATUS' ? statusOptions : cancelReasonOptions}
        selectedValue={statusStep === 'STATUS' ? selectedStatus : selectedReason}
        onChange={(value: string) => {
          if (statusStep === 'STATUS') {
            setSelectedStatus(value as 'COMPLETE' | 'CANCEL');
            return;
          }

          setSelectedReason(value);
        }}
        primaryLabel={statusStep === 'STATUS' ? 'Apply' : 'Continue to cancel'}
        onPrimaryClick={handleStatusPrimaryClick}
        secondaryLabel={statusStep === 'REASON' ? 'Back' : undefined}
        onSecondaryClick={
          statusStep === 'REASON'
            ? () => {
                setStatusStep('STATUS');
                setSelectedReason('');
                setReasonOtherText('');
              }
            : undefined
        }
        onClose={handleCloseStatusModal}
        loading={loading}
        extraTextValue={statusStep === 'REASON' ? 'OTHER' : undefined}
        extraTextLabel="Please specify"
        extraTextPlaceholder="Share your feedback"
        extraText={reasonOtherText}
        onExtraTextChange={statusStep === 'REASON' ? setReasonOtherText : undefined}
      />
    </div>
  );
};

export default CaseDetailsCard;
